import {IFilter} from '@toolify/server/src/services/FilterService/types/IFilter'
import {FilterItemType} from '@toolify/server/src/services/FilterService/enum/FilterItemType'
import {FilterPropertyOperator} from '@toolify/server/src/services/FilterService/enum/FilterPropertyOperator'
import {FilterPropertyPayloadType} from '@toolify/server/src/services/FilterService/enum/FilterPropertyPayloadType'
import {IFilterPropertyItem} from '@toolify/server/src/services/FilterService/types/IFilterPropertyItem'
import {
  IDatabaseGroupItemResponseInstance,
} from '@toolify/server/src/controllers/DatabaseItemController/validators/GetGroups/types/IDatabaseGroupItemResponseInstance'
import {
  IDatabasePropertyResponseInstance,
} from '@toolify/server/src/adapters/InstanceApiResponseAdapter/types/responseInstances/IDatabasePropertyResponseInstance'
import {GroupGranularityType} from '@toolify/server/src/services/DatabasePropertyService/types/GroupGranularityType'
import {
  isLocationBucket,
  isLocationIdentityKey,
  LOCATION_COMPONENT_BY_GRANULARITY,
  LOCATION_IDENTITY_FIELDS,
  LocationBucketLevel,
} from '@toolify/server/src/services/DatabasePropertyService/strategies/ColumnTypeStrategies/LocationPropertyTypeStrategy/locationBucket'
import {FilterGroupOperator} from '@toolify/server/src/services/FilterService/enum/FilterGroupOperator'
import {IGroupGranularityDescriptor} from '@toolify/client/src/modules/databases/utils/groupSortModes'
import {
  useValueTypeStrategy,
} from '@toolify/client/src/modules/databases/strategies/PropertyValueTypeStrategy/composables/useValueTypeStrategy'
import {getLocationBucketLevel} from '@toolify/client/src/modules/databases/utils/locationGroupGranularityDescriptor'

export function useLocationPropertyTypeStrategy() {
  const getGroupGranularityDescriptor = (property: IDatabasePropertyResponseInstance): IGroupGranularityDescriptor | null => {
    return useValueTypeStrategy(property.valueType).groupGranularity ?? null
  }

  const buildComponentSubProperty = (level: LocationBucketLevel): string =>
    `addressComponents.${LOCATION_COMPONENT_BY_GRANULARITY[level]}`

  const buildSubPropertyFilterItem = (
    group: string,
    subProperty: string,
    operator: FilterPropertyOperator,
    value: unknown,
  ): IFilterPropertyItem => ({
    type: FilterItemType.Property,
    id: group,
    subProperty,
    operator: operator === FilterPropertyOperator.Equals
      ? {
        type: operator,
        options: {
          isCaseSensitive: true,
        },
      }
      : {
        type: operator,
      },
    payload: {
      type: FilterPropertyPayloadType.Static,
      value,
    },
    isDisabled: false,
  })

  const getGroupFilter = (
    group: string,
    groupItem: IDatabaseGroupItemResponseInstance,
    _showAllDatabaseGroups: boolean,
    _property?: IDatabasePropertyResponseInstance,
    granularity?: GroupGranularityType | null,
  ): IFilter => {
    const value = groupItem?.value ?? null
    if(isLocationBucket(value)) {
      return buildSubPropertyFilterItem(group, buildComponentSubProperty(value.level), FilterPropertyOperator.Equals, value.value)
    }
    if(isLocationIdentityKey(value)) {
      return {
        type: FilterItemType.Group,
        operator: FilterGroupOperator.And,
        items: LOCATION_IDENTITY_FIELDS.map(field => buildSubPropertyFilterItem(group, field, FilterPropertyOperator.Equals, value[field] ?? null)),
      }
    }
    const level = getLocationBucketLevel(granularity)
    if(value === null && level !== null) {
      return buildSubPropertyFilterItem(group, buildComponentSubProperty(level), FilterPropertyOperator.Empty, null)
    }
    return null
  }

  return {
    getGroupFilter,
    getGroupGranularityDescriptor,
  }
}
